import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { getAppVersionPolicy, type AppVersionPolicy } from "~/services/common/appVersion";
import { getCurrentAppType, parseNativeAppInfo } from "%/appVersion";
import { openExternalBrowser } from "%/externalBrowser";
import { ForceUpdateScreen } from "@/common/ForceUpdateScreen";

interface AppVersionGateProps {
  children: ReactNode;
}

const getRecommendDismissKey = (policy: AppVersionPolicy) =>
  [
    "mung-app-update-recommend-dismissed",
    policy.appType,
    policy.platform,
    policy.latestVersion || policy.minVersion || "unknown",
    policy.latestBuild || policy.minBuild || "none",
  ].join(":");

export const AppVersionGate = ({ children }: AppVersionGateProps) => {
  const nativeAppInfo = useMemo(() => parseNativeAppInfo(), []);
  const appType = useMemo(() => getCurrentAppType(), []);
  const requestedImmediateUpdate = useRef(false);

  const [policy, setPolicy] = useState<AppVersionPolicy | null>(null);
  const [recommendDismissed, setRecommendDismissed] = useState(false);

  useEffect(() => {
    const platform = nativeAppInfo.platform;
    if (!nativeAppInfo.isNativeApp || !platform || !appType) return;

    let cancelled = false;

    const loadPolicy = async () => {
      try {
        const nextPolicy = await getAppVersionPolicy({
          appType,
          platform,
          version: nativeAppInfo.version,
          build: nativeAppInfo.build,
        });

        if (!cancelled) {
          setPolicy(nextPolicy);

          if (nextPolicy.recommendUpdate) {
            const dismissKey = getRecommendDismissKey(nextPolicy);
            setRecommendDismissed(sessionStorage.getItem(dismissKey) === "Y");
          }
        }
      } catch (error) {
        console.warn("[AppVersionGate] version policy check failed:", error);
      }
    };

    loadPolicy();

    return () => {
      cancelled = true;
    };
  }, [appType, nativeAppInfo.build, nativeAppInfo.isNativeApp, nativeAppInfo.platform, nativeAppInfo.version]);

  const forceUpdate = policy?.forceUpdate || policy?.updateType === "force";
  const recommendUpdate = policy?.recommendUpdate || policy?.updateType === "recommend";
  const showRecommend = !!policy && recommendUpdate && !forceUpdate && !recommendDismissed;

  useEffect(() => {
    if (!forceUpdate || nativeAppInfo.platform !== "android" || requestedImmediateUpdate.current) return;

    const requestImmediateUpdate = window.mung?.requestImmediateUpdate;
    if (typeof requestImmediateUpdate === "function") {
      requestedImmediateUpdate.current = true;
      requestImmediateUpdate();
    }
  }, [forceUpdate, nativeAppInfo.platform]);

  const openStore = () => {
    const storeUrl = policy?.storeUrl || policy?.fallbackStoreUrl;
    if (!storeUrl) return;
    openExternalBrowser(storeUrl);
  };

  const dismissRecommend = () => {
    if (!policy) return;

    sessionStorage.setItem(getRecommendDismissKey(policy), "Y");
    setRecommendDismissed(true);
  };

  if (forceUpdate && policy) {
    return (
      <ForceUpdateScreen
        policy={policy}
        force
        onUpdate={openStore}
      />
    );
  }

  return (
    <>
      {children}
      {showRecommend && (
        <ForceUpdateScreen
          policy={policy}
          force={false}
          onUpdate={openStore}
          onClose={dismissRecommend}
        />
      )}
    </>
  );
};
